import { KnowledgeArticle, createArticle } from '../types'

export const integrationsArticles: KnowledgeArticle[] = [
  createArticle({
    id: 'aws-setup',
    title: 'AWS Integration Setup',
    section: 'integrations',
    summary: 'Connect AWS using IAM credentials with SecurityAudit policy. The platform collects IAM, EC2, S3, RDS, CloudTrail data for compliance evidence.',
    content: `
# AWS Integration Setup

Connect your AWS account to automatically collect compliance evidence.

## Prerequisites

- AWS account with admin access (to create IAM credentials)
- SecurityAudit managed policy permissions

## Setup Steps

### 1. Create IAM User
1. Go to AWS Console → IAM → Users
2. Click "Add users"
3. Enter a name like "grc-platform-reader"
4. Select "Access key - Programmatic access"

### 2. Attach Policy
Attach the **SecurityAudit** managed policy. This provides read-only access to security configurations.

### 3. Get Credentials
After creating the user, save the:
- Access Key ID
- Secret Access Key

### 4. Add to Platform
1. Go to **Integrations** in the platform
2. Click **Add Integration** → AWS
3. Enter your Access Key ID and Secret Access Key
4. Select the regions to monitor
5. Click **Connect**

## What Gets Collected

- **IAM** - Users, roles, policies, MFA status
- **EC2** - Instances, security groups, AMIs
- **S3** - Buckets, encryption, public access
- **RDS** - Databases, encryption, backups
- **CloudTrail** - Audit logging status
- **VPC** - Network configurations

## Troubleshooting

**Access Denied errors**: Ensure the IAM user has the SecurityAudit policy attached.

**No data collected**: Check that you selected the correct regions.

**Partial data**: Some services may require additional permissions.
    `.trim(),
    tags: ['aws', 'cloud', 'integration', 'iam', 'security', 'evidence', 'automation'],
    relatedPages: ['/integrations'],
    icon: 'Cloud',
    order: 1,
  }),

  createArticle({
    id: 'azure-setup',
    title: 'Azure Integration Setup',
    section: 'integrations',
    summary: 'Connect Azure using a Service Principal with Reader role. The platform collects Azure AD, VMs, Storage, SQL, and security configurations.',
    content: `
# Azure Integration Setup

Connect your Azure account to automatically collect compliance evidence.

## Prerequisites

- Azure subscription with admin access
- Ability to create App Registrations in Azure AD

## Setup Steps

### 1. Create App Registration
1. Go to Azure Portal → Azure Active Directory → App Registrations
2. Click "New registration"
3. Enter a name like "GRC Platform"
4. Select "Accounts in this organizational directory only"
5. Click "Register"

### 2. Create Client Secret
1. Go to "Certificates & secrets"
2. Click "New client secret"
3. Set an expiration (recommend 12 months)
4. Copy the secret value immediately

### 3. Add API Permissions (for Entra ID data)
1. In your App Registration, go to **API permissions**
2. Click **Add a permission** → **Microsoft Graph** → **Application permissions**
3. Search for and add these permissions:
   - **User.Read.All** (Read all users' full profiles)
   - **Group.Read.All** (Read all groups)
4. Click **Add permissions**
5. Click **Grant admin consent for [Your Organization]** (requires admin)
6. Wait for the green checkmarks to appear

> **Note:** Without these permissions, the integration can still collect Azure resources (VMs, Storage, etc.) but won't be able to read Entra ID users and groups.

### 4. Assign Subscription Permissions (for Azure resources)
1. Go to Azure Portal → **Subscriptions** → Select your subscription
2. Click **Access Control (IAM)** in the left menu
3. Click **Add** → **Add role assignment**
4. Under "Role" tab, search for and select **"Reader"** → Click **Next**
5. Under "Members" tab:
   - Ensure **"User, group, or service principal"** is selected
   - Click **"+ Select members"**
   - **Search for your App Registration name** (e.g., "GRC Platform") in the search box
   - Your app will appear as an Application - click to select it
   - Click **Select**
6. Click **Review + assign**

> **Note:** The App Registration creates a Service Principal automatically. You must search by the exact name you gave your app - it won't appear in the default member list.

### 5. Get Configuration Values
You'll need:
- **Tenant ID** - Found in Azure AD Overview
- **Client ID** - Found in App Registration Overview
- **Client Secret** - Created in step 2
- **Subscription ID** - Found in Subscriptions

### 6. Add to Platform
1. Go to **Integrations** in the platform
2. Click **Add Integration** → Azure
3. Enter your Tenant ID, Client ID, Client Secret, and Subscription ID
4. Click **Connect**

## What Gets Collected

- **Entra ID (Azure AD)** - Users, groups, account status, sign-in info
- **Virtual Machines** - VMs, OS type, size, power state, network interfaces
- **Storage Accounts** - Encryption, HTTPS-only, TLS version, public access, network rules
- **SQL Databases** - Servers, databases, encryption status, zone redundancy
- **Network Security Groups** - Inbound/outbound rules, associated subnets and NICs
- **Web Apps (App Services)** - HTTPS-only, TLS version, FTPS state, runtime, always-on, managed identity, deployment slots
- **Key Vaults** - Soft delete, purge protection, RBAC authorization, network access, access policies count
    `.trim(),
    tags: ['azure', 'cloud', 'integration', 'service-principal', 'security', 'evidence'],
    relatedPages: ['/integrations'],
    icon: 'Cloud',
    order: 2,
  }),

  createArticle({
    id: 'atlassian-setup',
    title: 'Atlassian Integration (Jira & Confluence)',
    section: 'integrations',
    summary: 'Connect Jira and Confluence via OAuth. Sync tasks to Jira issues, import Confluence pages as policies, and enable two-way status updates.',
    content: `
# Atlassian Integration Setup

Connect Jira and Confluence to streamline your GRC workflows.

## Features

### Jira Integration
- Create Jira issues from GRC tasks
- Two-way sync for status updates
- Sync comments between systems
- Auto-create evidence when tickets are closed

### Confluence Integration
- Import pages as policies/procedures
- Map spaces to document types
- Import attachments as evidence
- Schedule recurring imports

## Setup Steps

### 1. Connect via OAuth
1. Go to **Settings** → **Integrations** → **Atlassian**
2. Click **Connect Atlassian**
3. A popup will open to authorize the connection
4. Sign in to your Atlassian account
5. Grant the requested permissions
6. The connection will be established

### 2. Configure Jira
1. Click on your connection
2. Go to the **Jira** tab
3. Click **Add Project** to map a Jira project
4. Configure:
   - Default issue type (Task, Story, etc.)
   - Enable/disable sync
   - Comment sync options
   - Auto-evidence on close

### 3. Configure Confluence
1. Go to the **Confluence** tab
2. Click **Add Space** to map a Confluence space
3. Configure:
   - Document type for imported pages
   - Import attachments option
   - Auto-import schedule

## Usage

### Creating Jira Issues
From any GRC task, click **Create Jira Issue** to create a linked ticket. Status changes sync automatically.

### Importing from Confluence
Click **Import** on a mapped space to pull pages into Documents. Pages become policies or procedures based on your mapping.
    `.trim(),
    tags: ['atlassian', 'jira', 'confluence', 'integration', 'tasks', 'documents', 'oauth'],
    relatedPages: ['/settings', '/tasks', '/documents'],
    icon: 'Link',
    order: 3,
  }),

  createArticle({
    id: 'user-directory-setup',
    title: 'User Directory Integration (Google & Entra)',
    section: 'integrations',
    summary: 'Import employees from Google Workspace or Microsoft Entra ID for training and phishing campaigns. Directory people are separate from GRC platform users.',
    content: `
# User Directory Integration

Import your employee directory from Google Workspace or Microsoft Entra ID to target users for security awareness training and phishing simulations.

## Important Concepts

**Directory People vs GRC Users**
- **Directory People**: Employees imported from your identity provider. They are targets for training/phishing but do NOT have GRC platform access.
- **GRC Users**: People who can log into this platform (admins, contributors, viewers).
- To give a directory person GRC access, use the "Promote to User" feature.

## Google Workspace Setup

### Prerequisites
- Google Workspace admin access
- Ability to create OAuth credentials in Google Cloud Console

### Steps

1. **Go to Google Cloud Console**
   - Navigate to [console.cloud.google.com](https://console.cloud.google.com)
   - Select or create a project

2. **Enable Admin SDK API**
   - Go to APIs & Services → Library
   - Search for "Admin SDK API"
   - Click Enable

3. **Create OAuth Credentials**
   - Go to APIs & Services → Credentials
   - Click "Create Credentials" → "OAuth client ID"
   - Select "Web application"
   - Add authorized redirect URI:
     \`\`\`
     https://app.clarantis.com/api/directory/oauth/google/callback
     \`\`\`
   - Save your Client ID and Client Secret

4. **Configure OAuth Consent Screen**
   - Go to OAuth consent screen
   - Add the required scopes:
     - \`https://www.googleapis.com/auth/admin.directory.user.readonly\`
     - \`https://www.googleapis.com/auth/admin.directory.group.readonly\`

5. **Add to Platform**
   - Go to Integrations → User Directory
   - Click "Add Directory" → Google Workspace
   - Enter your Client ID and Client Secret
   - Complete the OAuth authorization flow

## Microsoft Entra ID Setup

### Prerequisites
- Azure AD / Entra ID admin access
- Ability to create App Registrations

### Steps

1. **Create App Registration**
   - Go to [Azure Portal](https://portal.azure.com) → Azure Active Directory → App Registrations
   - Click "New registration"
   - Name: "Clarantis GRC - Directory Sync"
   - Supported account types: "Accounts in this organizational directory only"
   - Click Register

2. **Add Redirect URI**
   - Go to Authentication → Add a platform → Web
   - Add redirect URI:
     \`\`\`
     https://app.clarantis.com/api/directory/oauth/entra/callback
     \`\`\`

3. **Create Client Secret**
   - Go to Certificates & secrets
   - Click "New client secret"
   - Set description and expiration
   - **Copy the secret value immediately** (you won't see it again)

4. **Add API Permissions**
   - Go to API permissions → Add a permission
   - Select Microsoft Graph → Application permissions
   - Add these permissions:
     - **User.Read.All** - Read all users' full profiles
     - **Group.Read.All** - Read all groups
   - Click "Grant admin consent for [Your Organization]"
   - Wait for green checkmarks

5. **Get Configuration Values**
   - **Tenant ID**: Found in Overview
   - **Client ID**: Found in Overview (Application ID)
   - **Client Secret**: Created in step 3

6. **Add to Platform**
   - Go to Integrations → User Directory
   - Click "Add Directory" → Microsoft Entra ID
   - Enter your Client ID and Client Secret
   - Complete the OAuth authorization flow

## Sync Behavior

- **Full Sync**: Every sync fetches all users and groups from your identity provider
- **New users**: Added to directory_people table
- **Existing users**: Updated with latest information
- **Removed users**: Optionally disabled (configurable)
- **Groups**: Synced with membership information

## Troubleshooting

**"Access Denied" or "Insufficient Permissions"**
- Ensure admin consent was granted for API permissions
- For Google: Ensure the authorizing user is a Workspace admin

**"Invalid redirect URI"**
- Verify the redirect URI matches exactly:
  - Google: \`https://app.clarantis.com/api/directory/oauth/google/callback\`
  - Entra: \`https://app.clarantis.com/api/directory/oauth/entra/callback\`

**Sync shows 0 users**
- Check API permissions are granted
- Verify the account has access to read users/groups

**Users not appearing in Training/Phishing**
- Users are imported as Directory People, not GRC Users
- Check the Directory People page in Settings
    `.trim(),
    tags: ['directory', 'google', 'entra', 'azure', 'oauth', 'users', 'training', 'phishing', 'identity'],
    relatedPages: ['/integrations', '/settings'],
    icon: 'Users',
    order: 4,
  }),
]
